import {Observable} from 'rxjs';
import i18next from 'i18next';
import {Range, type List} from 'immutable';

import {combineEpics} from 'web-app/util/redux-observable';
import {showDeleteDialogObservable, DialogChoice} from 'web-app/react/dialog';
import * as GlobalEventActions from 'web-app/react/global-event/actions';

import * as Actions from './actions';
import * as Api from './api';
import {type IClosingDay} from './types';

export const fetchClosingDaysObservable = (institutionId: string) =>
    Api.fetch(institutionId).map(response => Actions.fetchSuccess.action(response));

const fetch = action$ =>
    action$
        .ofType(Actions.fetch.type)
        .mergeMap(({payload}: ReturnType<typeof Actions.fetch.action>) =>
            fetchClosingDaysObservable(payload.institutionId).catch(e => Observable.of(Actions.fetchFailed.action(e))),
        );

export const deleteClosingDayObservable = (closingDayId: string) =>
    Api.deleteClosingDay(closingDayId).map(() => Actions.deleteClosingDaySuccess.action(closingDayId));

const deleteClosingDay = action$ =>
    action$
        .ofType(Actions.deleteClosingDay.type)
        .mergeMap(({payload}: ReturnType<typeof Actions.deleteClosingDay.action>) =>
            showDeleteDialogObservable({
                title: '',
                description: i18next.t('calendar.confirmClosingDayDelete'),
            }).mergeMap(selection => {
                if (selection === DialogChoice.OK) {
                    return deleteClosingDayObservable(payload.closingDayId)
                        .flatMap(action => [
                            GlobalEventActions.updateSuccess.action(i18next.t('calendar.closingDayDeleted')),
                            action,
                        ])
                        .catch(() => Observable.of(Actions.deleteClosingDayFailed.action(payload.closingDayId)));
                }
                return Observable.of(Actions.deleteClosingDayAborted.action(payload.closingDayId));
            }),
        );

const deleteClosingDaysObservable = (closingDays: List<IClosingDay>) =>
    Api.deleteClosingDays(closingDays).map(() => Actions.deleteClosingIntervalSuccess.action(closingDays));

const deleteClosingInterval = action$ =>
    action$
        .ofType(Actions.deleteClosingInterval.type)
        .mergeMap(({payload}: ReturnType<typeof Actions.deleteClosingInterval.action>) =>
            showDeleteDialogObservable({
                title: '',
                description: i18next.t('calendar.confirmClosingDaysDelete'),
            }).mergeMap(selection => {
                if (selection === DialogChoice.OK) {
                    return deleteClosingDaysObservable(payload.closingDays)
                        .flatMap(action => [
                            GlobalEventActions.updateSuccess.action(i18next.t('calendar.closingDaysDeleted')),
                            action,
                        ])
                        .catch(() => Observable.of(Actions.deleteClosingIntervalFailed.action(payload.closingDays)));
                }

                return Observable.of(Actions.deleteClosingIntervalAborted.action(payload.closingDays));
            }),
        );

const addClosingDays = action$ =>
    action$
        .ofType(Actions.addClosingDays.type)
        .mergeMap(({payload}: ReturnType<typeof Actions.addClosingDays.action>) => {
            const {startDay, endDay, institutionId, reason, dayType} = payload;
            const durationDays = endDay.diff(startDay, 'days') + 1;

            const days = Range(0, durationDays)
                .map(i =>
                    Api.addClosingDay(
                        institutionId,
                        startDay.clone().add(i, 'days').format('YYYY-MM-DD'),
                        reason,
                        dayType,
                    ),
                )
                .toArray();

            return Observable.zip(...days)
                .flatMap(newClosingDays => [
                    GlobalEventActions.updateSuccess.action(
                        i18next.t('calendar.closingDayCreated', {count: durationDays}),
                    ),
                    Actions.addClosingDaysSuccess.action(newClosingDays),
                ])
                .catch(e => Observable.of(Actions.addClosingDaysFailed.action(e)));
        });

export default combineEpics(fetch, deleteClosingDay, deleteClosingInterval, addClosingDays);
